import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  Search,
  Loader2,
  ChevronDown,
  ChevronUp,
  Package,
  RefreshCw,
  AlertCircle,
  Trash2,
  CloudDownload,
  Hash,
  Scale,
  ChefHat, Salad, Flame, UtensilsCrossed, Leaf, IceCream, Wheat, Martini, CupSoda, Wine
} from 'lucide-react';
import { recipesAPI, odooAPI, formatApiError } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { TileGrid, BackButton, SectionHeader } from '../components/common/TileGrid';

const RECIPE_ORDER = ['PREPARATIONS', 'MEZZES FROIDS', 'MEZZES CHAUDS', 'PLATS', 'SALADES', 'DESSERTS', 'HPP', 'EPICERIE', 'MENUS', 'COCKTAILS', 'BOISSONS', 'CAVE', 'AUTRES'];
const RECIPE_CFG = {
  PREPARATIONS: { icon: ChefHat, color: 'text-violet-600', bg: 'bg-violet-500/10', border: 'border-violet-500/30' },
  'MEZZES FROIDS': { icon: Salad, color: 'text-emerald-600', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  'MEZZES CHAUDS': { icon: Flame, color: 'text-red-600', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  PLATS: { icon: UtensilsCrossed, color: 'text-orange-600', bg: 'bg-orange-500/10', border: 'border-orange-500/30' },
  SALADES: { icon: Leaf, color: 'text-lime-700', bg: 'bg-lime-50', border: 'border-lime-300' },
  DESSERTS: { icon: IceCream, color: 'text-neon-pink', bg: 'bg-neon-pink/10', border: 'border-neon-pink/30' },
  HPP: { icon: Package, color: 'text-sky-600', bg: 'bg-sky-500/10', border: 'border-sky-500/30' },
  EPICERIE: { icon: Wheat, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-300' },
  MENUS: { icon: BookOpen, color: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-300' },
  COCKTAILS: { icon: Martini, color: 'text-fuchsia-600', bg: 'bg-fuchsia-500/10', border: 'border-fuchsia-500/30' },
  BOISSONS: { icon: CupSoda, color: 'text-teal-600', bg: 'bg-teal-500/10', border: 'border-teal-500/30' },
  CAVE: { icon: Wine, color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-300' },
  AUTRES: { icon: BookOpen, color: 'text-gray-500', bg: 'bg-gray-100', border: 'border-gray-200' },
};
const recipeCfg = (c) => RECIPE_CFG[c] || RECIPE_CFG.AUTRES;

const RecipesPage = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_manager;

  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [odooStatus, setOdooStatus] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRecipes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [statusRes, recipesRes] = await Promise.all([
        odooAPI.status(),
        recipesAPI.getAll()
      ]);
      setOdooStatus(statusRes.data);
      setRecipes(recipesRes.data.data || []);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await recipesAPI.sync();
      toast.success(res.data.message || 'Synchronisation terminée');
      await fetchRecipes();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await recipesAPI.delete(deleteTarget.odoo_bom_id);
      toast.success(`"${deleteTarget.product_name}" supprimée`);
      setRecipes(prev => prev.filter(r => r.odoo_bom_id !== deleteTarget.odoo_bom_id));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const filteredRecipes = recipes.filter(r =>
    (selectedCategory === 'all' || (r.category || 'AUTRES') === selectedCategory) &&
    (r.product_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.code?.toLowerCase().includes(search.toLowerCase()))
  );
  const categoryCounts = recipes.reduce((acc, r) => { const c = r.category || 'AUTRES'; acc[c] = (acc[c] || 0) + 1; return acc; }, {});
  const categoryList = Object.keys(categoryCounts).sort((a, b) => (RECIPE_ORDER.indexOf(a) === -1 ? 99 : RECIPE_ORDER.indexOf(a)) - (RECIPE_ORDER.indexOf(b) === -1 ? 99 : RECIPE_ORDER.indexOf(b)) || a.localeCompare(b));
  const showTiles = search.trim() === '' && selectedCategory === 'all';

  const stats = {
    total: recipes.length,
    withComponents: recipes.filter(r => r.component_count > 0).length,
    manufacture: recipes.filter(r => r.type === 'manufacture').length,
  };

  return (
    <div className="min-h-screen p-3 md:p-8 pb-24 md:pb-8" data-testid="recipes-page">
      {/* Header */}
      <div className="max-w-5xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-1">
              Recettes & Nomenclatures
            </h1>
            <p className="text-gray-500 text-sm">
              {stats.total} nomenclatures depuis Odoo 18
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                onClick={handleSync}
                disabled={syncing}
                className="rounded-xl bg-neon-pink hover:bg-neon-pink/90 text-gray-900 shadow-neon-pink gap-2"
                data-testid="sync-recipes-btn"
              >
                {syncing ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <CloudDownload size={16} />
                )}
                {syncing ? 'Sync...' : 'Synchroniser Odoo'}
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={fetchRecipes}
              className="rounded-xl bg-white border-gray-200 text-gray-700 hover:bg-gray-100"
              data-testid="refresh-recipes-btn"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>

        {/* Stats row */}
        <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-6 text-center">
          <p className="text-3xl font-bold text-amber-700" data-testid="stat-total">{stats.total}</p>
          <p className="text-xs text-gray-400 mt-1">Nomenclatures disponibles</p>
        </div>

        {/* Odoo Status */}
        <div className={`rounded-2xl p-4 mb-6 border ${odooStatus?.connected ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-red-500/10 border-red-500/20'}`}
          data-testid="odoo-status">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${odooStatus?.connected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
            <span className={`font-medium text-sm ${odooStatus?.connected ? 'text-emerald-600' : 'text-red-600'}`}>
              {odooStatus?.connected ? 'Connecté à Odoo' : 'Non connecté à Odoo'}
            </span>
            <span className="text-xs text-gray-400">
              {odooStatus?.url}
            </span>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            type="text"
            placeholder="Rechercher par nom ou code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-12 h-12 rounded-xl bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-neon-pink"
            data-testid="search-recipes-input"
          />
          {search && (
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400">
              {filteredRecipes.length} résultat{filteredRecipes.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-neon-pink animate-spin mb-4" />
            <p className="text-gray-500">Chargement des recettes...</p>
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold text-red-600 mb-2">Erreur</h3>
            <p className="text-red-300 mb-4">{error}</p>
            <Button onClick={fetchRecipes} variant="outline" className="border-red-500/30 text-red-600 hover:bg-red-500/10">
              Réessayer
            </Button>
          </div>
        ) : recipes.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">
              Aucune recette en cache
            </h3>
            <p className="text-gray-500 mb-6">
              Cliquez sur "Synchroniser Odoo" pour importer les 244 nomenclatures
            </p>
            {isAdmin && (
              <Button onClick={handleSync} disabled={syncing} className="bg-neon-pink hover:bg-neon-pink/90 rounded-full gap-2 shadow-neon-pink">
                <CloudDownload size={16} />
                Synchroniser maintenant
              </Button>
            )}
          </div>
        ) : showTiles ? (
          <TileGrid testId="recipe-tiles" onSelect={setSelectedCategory} tiles={categoryList.map(c => { const cfg = recipeCfg(c); return { id: c, testPrefix: 'recipe-tile', label: c, sub: `${categoryCounts[c]} recette${categoryCounts[c] > 1 ? 's' : ''}`, icon: cfg.icon, cfg }; })} />
        ) : filteredRecipes.length === 0 ? (
          <div className="text-center py-20">
            {selectedCategory !== 'all' && <div className="flex justify-center mb-6"><BackButton onClick={() => setSelectedCategory('all')} testId="recipe-back-btn" /></div>}
            <Search className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">
              Aucun résultat
            </h3>
            <p className="text-gray-500">
              Modifiez votre recherche "{search}"
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {selectedCategory !== 'all' && <BackButton onClick={() => setSelectedCategory('all')} testId="recipe-back-btn" />}
            {Object.entries(filteredRecipes.reduce((acc, r) => { const c = r.category || 'AUTRES'; (acc[c] = acc[c] || []).push(r); return acc; }, {})).map(([cat, list]) => {
              const cfg = recipeCfg(cat);
              return (
            <div key={cat} className={`bg-white border-2 ${cfg.border} rounded-3xl overflow-hidden`} data-testid={`recipe-section-${cat}`}>
              <SectionHeader icon={cfg.icon} cfg={cfg} label={cat} sub={`${list.length} recette${list.length > 1 ? 's' : ''}`} />
            <div className="divide-y divide-gray-100">
            <AnimatePresence>
              {list.map((recipe, index) => (
                <motion.div
                  key={recipe.odoo_bom_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ delay: Math.min(index * 0.02, 0.5) }}
                >
                  <Collapsible
                    open={expandedId === recipe.odoo_bom_id}
                    onOpenChange={() => setExpandedId(expandedId === recipe.odoo_bom_id ? null : recipe.odoo_bom_id)}
                  >
                    <div className="bg-white overflow-hidden transition-colors"
                      data-testid={`recipe-card-${recipe.odoo_bom_id}`}>
                      <div className="flex items-center">
                        <CollapsibleTrigger className="flex-1 p-4 md:p-5 flex items-center justify-between hover:bg-gray-50 transition-colors">
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <div className={`w-11 h-11 ${cfg.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                              <BookOpen className={cfg.color} size={20} />
                            </div>
                            <div className="text-left min-w-0 flex-1">
                              <h3 className="font-heading font-bold text-gray-900 truncate text-sm">
                                {recipe.product_name}
                              </h3>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <Badge className="bg-amber-50 text-amber-700 border border-amber-300 text-[10px]">
                                  {recipe.type === 'manufacture' ? 'Fabrication' : recipe.type === 'subcontract' ? 'Sous-traitance' : recipe.type}
                                </Badge>
                                {recipe.code && (
                                  <Badge variant="outline" className="border-gray-200 text-gray-500 text-[10px] gap-1">
                                    <Hash size={10} />{recipe.code}
                                  </Badge>
                                )}
                                <span className="text-xs text-gray-400">
                                  {recipe.component_count || 0} ingrédient{(recipe.component_count || 0) !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-2">
                            {expandedId === recipe.odoo_bom_id ? (
                              <ChevronUp className="text-gray-500" size={20} />
                            ) : (
                              <ChevronDown className="text-gray-500" size={20} />
                            )}
                          </div>
                        </CollapsibleTrigger>
                        {isAdmin && (
                          <button
                            onClick={() => setDeleteTarget(recipe)}
                            className="p-2 mr-3 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-500/10 transition-colors"
                            data-testid={`delete-recipe-btn-${recipe.odoo_bom_id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>

                      <CollapsibleContent>
                        <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                          {/* Recipe info */}
                          <div className="flex items-center gap-4 mb-4 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <Scale size={12} />
                              Qté: {recipe.quantity} {recipe.quantity === 1 ? 'unité' : 'unités'}
                            </span>
                            <span>Odoo ID: #{recipe.odoo_bom_id}</span>
                          </div>

                          <h4 className="font-heading font-bold text-xs text-gray-400 uppercase tracking-[0.2em] mb-3">
                            Composants / Ingrédients
                          </h4>
                          {recipe.components && recipe.components.length > 0 ? (
                            <div className="grid gap-2">
                              {recipe.components.map((comp) => (
                                <div
                                  key={`${comp.product_name}-${comp.quantity}-${comp.unit}`}
                                  className="flex items-center justify-between py-3 px-4 bg-gray-50 border border-gray-100 rounded-xl"
                                >
                                  <div className="flex items-center gap-3">
                                    <Package size={14} className="text-gray-400 flex-shrink-0" />
                                    <span className="text-sm font-medium text-gray-900">{comp.product_name}</span>
                                  </div>
                                  <span className="text-sm text-amber-700 font-medium flex-shrink-0 ml-2">
                                    {comp.quantity} {comp.unit}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-400 text-center py-4 text-sm">
                              Aucun composant défini dans cette nomenclature
                            </p>
                          )}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                </motion.div>
              ))}
            </AnimatePresence>
            </div>
            </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-gray-900">
              Supprimer cette recette ?
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Cette action masquera la recette de la liste. Elle pourra être restaurée lors d'une prochaine synchronisation.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-gray-100 border border-gray-100 rounded-xl p-4 my-4">
            <p className="font-medium text-gray-900">{deleteTarget?.product_name}</p>
            <p className="text-xs text-gray-400 mt-1">
              {deleteTarget?.component_count || 0} composants - Odoo #{deleteTarget?.odoo_bom_id}
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              className="flex-1 rounded-full bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200"
              data-testid="cancel-delete-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="flex-1 bg-red-500 hover:bg-red-600 rounded-full text-gray-900"
              data-testid="confirm-delete-recipe-btn"
            >
              {deleting ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                'Supprimer'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecipesPage;
